import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PerformanceMetrics {
  overallAverage: number;
  totalTests: number;
  subjectPerformance: { [key: string]: number };
  recentScores: number[];
  lastTestDate: string | null;
  trend: 'improving' | 'declining' | 'stable';
}

export function useChildPerformance(studentEmail: string) {
  const [performance, setPerformance] = useState<PerformanceMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPerformance = async () => {
      if (!studentEmail) {
        setIsLoading(false);
        return;
      }

      try {
        // Use the security definer function to get child's test sessions
        const { data: sessions, error } = await supabase.rpc('get_child_test_sessions', {
          child_email: studentEmail
        });

        if (error) {
          console.error('Error fetching child performance:', error);
          setIsLoading(false);
          return;
        }

        if (!sessions || sessions.length === 0) {
          setPerformance({
            overallAverage: 0,
            totalTests: 0,
            subjectPerformance: {},
            recentScores: [],
            lastTestDate: null,
            trend: 'stable'
          });
          setIsLoading(false);
          return;
        }

        // Calculate metrics
        const scores = sessions.map((s: any) => (s.score / s.total_questions) * 100);
        const overallAverage = scores.reduce((a: number, b: number) => a + b, 0) / scores.length;
        
        const subjectPerformance: { [key: string]: number } = {};
        sessions.forEach((session: any) => {
          const subjectName = session.subject_name || 'Unknown';
          const score = (session.score / session.total_questions) * 100;
          
          if (!subjectPerformance[subjectName]) {
            subjectPerformance[subjectName] = score;
          } else {
            subjectPerformance[subjectName] = (subjectPerformance[subjectName] + score) / 2;
          }
        });

        // Calculate trend
        const recentScores = scores.slice(0, 5);
        let trend: 'improving' | 'declining' | 'stable' = 'stable';
        if (recentScores.length >= 3) {
          const firstHalf = recentScores.slice(0, Math.floor(recentScores.length / 2));
          const secondHalf = recentScores.slice(Math.floor(recentScores.length / 2));
          const firstAvg = firstHalf.reduce((a: number, b: number) => a + b, 0) / firstHalf.length;
          const secondAvg = secondHalf.reduce((a: number, b: number) => a + b, 0) / secondHalf.length;
          
          if (secondAvg > firstAvg + 5) trend = 'improving';
          else if (secondAvg < firstAvg - 5) trend = 'declining';
        }

        setPerformance({
          overallAverage: Math.round(overallAverage),
          totalTests: sessions.length,
          subjectPerformance,
          recentScores: recentScores.slice(0, 10),
          lastTestDate: sessions[0]?.created_at || null,
          trend
        });
      } catch (error) {
        console.error('Error fetching child performance:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPerformance();
  }, [studentEmail]);

  return { performance, isLoading };
}
